import React, { useState, useEffect, useRef } from 'react';

const CategorySelect = ({ categories, value, onChange, label = 'Category', required = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const containerRef = useRef(null);

  // Build category list with hierarchy info
  const buildCategoryTree = () => {
    return categories
      .map(cat => {
        // Check if this category has children
        const hasChildren = categories.some(c => c.parent_id === cat.id);
        const isParent = cat.parent_id === null && hasChildren;
        
        return {
          ...cat,
          isParent,
          isSelectable: !isParent, // Only selectable if it's not a parent
          parent: cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null,
        };
      })
      .filter(cat => cat.isSelectable); // Only keep selectable (child) categories
  };

  const categoryTree = buildCategoryTree();

  // Filter categories based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOptions(categoryTree);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = categoryTree.filter(cat => {
      const nameMatch = cat.name.toLowerCase().includes(term);
      const descMatch = cat.description?.toLowerCase().includes(term) || false;
      const parentMatch = cat.parent?.name.toLowerCase().includes(term) || false;
      return nameMatch || descMatch || parentMatch;
    });

    setFilteredOptions(filtered);
  }, [searchTerm, categories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get selected category label
  const getSelectedLabel = () => {
    if (!value) return 'Select Category';
    const selected = categories.find(c => c.id === parseInt(value));
    if (!selected) return 'Select Category';
    
    const parent = selected.parent_id ? 
      categories.find(c => c.id === selected.parent_id) : null;
    
    return parent ? `${parent.name} - ${selected.name}` : selected.name;
  };

  const handleSelect = (cat) => {
    if (cat.isSelectable) {
      onChange(cat.id);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const styles = {
    container: {
      position: 'relative',
      width: '100%',
    },
    input: {
      padding: '10px 12px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      fontSize: '14px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      color: '#0f172a',
      backgroundColor: '#fff',
      width: '100%',
      cursor: 'pointer',
    },
    searchInput: {
      padding: '10px 12px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      fontSize: '14px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
      color: '#0f172a',
      backgroundColor: '#fff',
      width: '100%',
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      zIndex: 10,
      marginTop: '4px',
      maxHeight: '300px',
      overflowY: 'auto',
    },
    option: {
      padding: '10px 12px',
      cursor: 'pointer',
      fontSize: '13px',
      borderBottom: '1px solid #f1f5f9',
      transition: '0.15s',
      backgroundColor: '#fff',
      color: '#1e293b',
    },
    optionHover: {
      backgroundColor: '#f8fafc',
    },
    optionDisabled: {
      padding: '10px 12px',
      fontSize: '13px',
      borderBottom: '1px solid #f1f5f9',
      color: '#cbd5e1',
      backgroundColor: '#f8fafc',
      cursor: 'not-allowed',
    },
    optionSelected: {
      backgroundColor: '#eef2ff',
      color: '#4f46e5',
      fontWeight: '600',
    },
    optionLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    parentBadge: {
      display: 'inline-block',
      backgroundColor: '#e2e8f0',
      color: '#64748b',
      fontSize: '11px',
      padding: '2px 6px',
      borderRadius: '3px',
      marginRight: '4px',
    },
    hierarchyText: {
      fontSize: '12px',
      color: '#64748b',
      marginLeft: '20px',
    },
  };

  return (
    <div style={styles.container} ref={containerRef}>
      <input
        type="text"
        placeholder={getSelectedLabel()}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        style={{
          ...styles.searchInput,
          ...(isOpen && { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }),
        }}
      />

      {isOpen && (
        <div style={styles.dropdown}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#64748b' }}>
              No categories found
            </div>
          ) : (
            filteredOptions.map(cat => (
              <div
                key={cat.id}
                onClick={() => handleSelect(cat)}
                style={{
                  ...styles.option,
                  ...(value === String(cat.id) && styles.optionSelected),
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = value === String(cat.id) ? '#eef2ff' : '#fff';
                }}
              >
                <span>
                  {cat.parent && (
                    <span style={{ color: '#64748b' }}>
                      {cat.parent.name} - 
                    </span>
                  )}
                  {cat.name}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Hidden select for form submission fallback */}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        style={{ display: 'none' }}
      >
        <option value="">Select Category</option>
        {categoryTree.map(cat => (
          <option key={cat.id} value={cat.id}>
            {cat.parent ? `${cat.parent.name} - ${cat.name}` : cat.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CategorySelect;
